// File: src/ContainersManager.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, LinearProgress, Stack, TextField, Typography, Chip,
  MenuItem, Alert, Paper, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { db } from './firebase';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, orderBy, query
} from 'firebase/firestore';

const CATEGORIES = [
  { value: 'bowl', label: 'Bowl' },
  { value: 'box', label: 'Box' },
  { value: 'cup', label: 'Cup' },
  { value: 'lid', label: 'Lid' },
  { value: 'set', label: 'Set (container + lid)' },
  { value: 'other', label: 'Other' }
];

const asMap = (arr, key = 'id') =>
  arr.reduce((m, x) => (m[String(x[key]).toLowerCase()] = x, m), {});
const getLabel = (map, id, labelKey = 'label') => {
  if (!id) return '';
  const hit = map[String(id).toLowerCase()];
  return hit ? (hit[labelKey] ?? hit.name ?? '') : '';
};

const ContainersManager = () => {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [rows, setRows] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const vendorMap = useMemo(() => asMap(vendors), [vendors]);

  const emptyForm = useMemo(() => ({
    label: '',
    category: 'set',
    sku: '',
    vendor_id: '',
    vendor_label: '',
    size_refs: [],        // array of size_ids this container matches (optional)
    dishwasher_safe: false,
    notes: ''
  }), []);

  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try {
      setLoading(true);
      setErr('');

      // Containers
      const snap = await getDocs(query(collection(db, 'packaging_containers'), orderBy('label')));
      setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Sizes (to show which sizes each container matches)
      const sizeSnap = await getDocs(query(collection(db, 'beverage_sizes'), orderBy('label')));
      setSizes(sizeSnap.docs.map(d => ({ id: d.id, label: d.data().label ?? d.id, ...d.data() })));

      // Vendors (dropdown source)
      const vendorSnap = await getDocs(query(collection(db, 'vendors'), orderBy('name')));
      setVendors(vendorSnap.docs.map(d => ({ id: d.id, label: d.data().name ?? d.id, ...d.data() })));

    } catch (e) {
      console.error(e);
      setErr(e?.message || 'Failed to load containers/vendors.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleOpen = (row = null) => {
    setEditing(row?.id || null);
    setForm(row ? {
      label: row.label || '',
      category: row.category || 'set',
      sku: row.sku || '',
      vendor_id: row.vendor_id || '',
      vendor_label: row.vendor_label || '',
      size_refs: Array.isArray(row.size_refs) ? row.size_refs : [],
      dishwasher_safe: !!row.dishwasher_safe,
      notes: row.notes || ''
    } : emptyForm);
    setOpen(true);
  };

  const handleClose = () => { setOpen(false); setEditing(null); };

  const save = async () => {
    const payload = { ...form };

    // Auto-fill vendor_label from vendor_id
    if (payload.vendor_id) {
      payload.vendor_label = getLabel(vendorMap, payload.vendor_id, 'label');
    } else {
      delete payload.vendor_label;
    }

    try {
      if (editing) {
        await updateDoc(doc(db, 'packaging_containers', editing), payload);
      } else {
        await addDoc(collection(db, 'packaging_containers'), payload);
      }
      handleClose();
      await load();
    } catch (e) {
      alert(e?.message || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this container?')) return;
    try {
      await deleteDoc(doc(db, 'packaging_containers', id));
      await load();
    } catch (e) {
      alert(e?.message || 'Delete failed');
    }
  };

  if (loading) return (<Box p={2}><LinearProgress /></Box>);

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Packaging Containers</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>New Container</Button>
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      <Stack spacing={1}>
        {rows.map(row => (
          <Paper key={row.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>{row.label}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {(CATEGORIES.find(c => c.value === row.category)?.label || row.category)}
                  {row.sku ? ` • SKU: ${row.sku}` : ''}
                  {row.vendor_label ? ` • Vendor: ${row.vendor_label}` : (row.vendor_id ? ` • Vendor: ${row.vendor_id}` : '')}
                </Typography>

                {!!row.size_refs?.length && (
                  <Box mt={1}>
                    <Typography variant="caption" color="text.secondary">Matches sizes:</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} mt={0.5}>
                      {row.size_refs.map(sid => {
                        const s = sizes.find(x => x.id === sid);
                        return <Chip key={sid} label={s?.label || sid} size="small" />;
                      })}
                    </Stack>
                  </Box>
                )}

                {row.notes && (<Typography variant="body2" mt={1}>{row.notes}</Typography>)}
              </Box>
              <Box>
                <IconButton onClick={() => handleOpen(row)}><EditIcon /></IconButton>
                <IconButton color="error" onClick={() => remove(row.id)}><DeleteIcon /></IconButton>
              </Box>
            </Stack>
          </Paper>
        ))}

        {!rows.length && (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography>No containers yet.</Typography>
          </Paper>
        )}
      </Stack>

      {/* Editor */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {editing ? 'Edit Container' : 'New Container'}
          <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Label"
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              fullWidth
              required
            />

            <TextField
              select
              label="Category"
              value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              fullWidth
            >
              {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="SKU"
                value={form.sku}
                onChange={e => setForm(f => ({ ...f, sku: e.target.value }))}
                fullWidth
              />
              {/* Vendor dropdown from Vendors collection */}
              <TextField
                select
                label="Vendor"
                value={form.vendor_id}
                onChange={e => setForm(f => ({
                  ...f,
                  vendor_id: e.target.value,
                  vendor_label: getLabel(vendorMap, e.target.value, 'label')
                }))}
                fullWidth
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {vendors.map(v => (
                  <MenuItem key={v.id} value={v.id}>{v.label}</MenuItem>
                ))}
              </TextField>
            </Stack>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Matches Sizes (optional)</Typography>
              <TextField
                select
                SelectProps={{ multiple: true }}
                label="Size IDs"
                value={form.size_refs}
                onChange={e => setForm(f => ({ ...f, size_refs: e.target.value }))}
                fullWidth
              >
                {sizes.map(s => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
              </TextField>
            </Box>

            <Divider />

            <TextField
              label="Notes"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContainersManager;
